"use client";

import { useState } from "react";
import { useMutation } from "convex/react";
import { ConvexError } from "convex/values";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";

import type { EnrichmentFeedbackCategory } from "@soonlist/backend/convex/eventFeedbackCategories";
import { api } from "@soonlist/backend/convex/_generated/api";
import { ENRICHMENT_FEEDBACK_CATEGORIES } from "@soonlist/backend/convex/eventFeedbackCategories";
import { Button } from "@soonlist/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@soonlist/ui/dialog";
import { ANALYTICS_EVENTS } from "@soonlist/validators";

import { Check } from "~/components/icons";
import { cn } from "~/lib/utils";

const CATEGORY_LABELS: Record<EnrichmentFeedbackCategory, string> = {
  description: "Description",
  price_tickets: "Price or tickets link",
  featured: "Featured artists or links",
  vibe_good_to_know: "What to expect or Good to know",
  other: "Something else",
};

function errorMessage(error: unknown): string {
  if (error instanceof ConvexError && typeof error.data === "string") {
    return error.data;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong. Please try again.";
}

interface ReportEnrichmentDialogProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReportEnrichmentDialog({
  eventId,
  open,
  onOpenChange,
}: ReportEnrichmentDialogProps) {
  const [selectedCategories, setSelectedCategories] = useState<
    EnrichmentFeedbackCategory[]
  >([]);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reportEnrichment = useMutation(api.eventFeedback.reportEnrichment);
  const posthog = usePostHog();

  function toggleCategory(category: EnrichmentFeedbackCategory) {
    setSelectedCategories((previous) =>
      previous.includes(category)
        ? previous.filter((value) => value !== category)
        : [...previous, category],
    );
  }

  function resetState() {
    setSelectedCategories([]);
    setNote("");
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      resetState();
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit() {
    if (selectedCategories.length === 0 || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      const trimmedNote = note.trim();
      await reportEnrichment({
        eventId,
        categories: selectedCategories,
        note: trimmedNote || undefined,
      });
      posthog.capture(ANALYTICS_EVENTS.ENRICHMENT_REPORT_SUBMITTED, {
        event_id: eventId,
        categories: selectedCategories,
        has_note: trimmedNote.length > 0,
      });
      toast.success("Thanks — we'll take a look");
      resetState();
      onOpenChange(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[80dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Report incorrect info</DialogTitle>
          <DialogDescription>
            What's incorrect? Select one or more.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {ENRICHMENT_FEEDBACK_CATEGORIES.map((category) => {
            const checked = selectedCategories.includes(category);
            return (
              <button
                key={category}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggleCategory(category)}
                className={cn(
                  "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm font-medium text-neutral-1 transition-colors",
                  checked
                    ? "border-interactive-1 bg-interactive-3"
                    : "border-neutral-3 hover:border-neutral-2",
                )}
              >
                <span
                  className={cn(
                    "flex size-5 shrink-0 items-center justify-center rounded-md border",
                    checked
                      ? "border-interactive-1 bg-interactive-1 text-white"
                      : "border-neutral-3 bg-white",
                  )}
                >
                  {checked && <Check className="size-3.5" />}
                </span>
                {CATEGORY_LABELS[category]}
              </button>
            );
          })}
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-neutral-1">
            Additional notes (optional)
          </span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add anything that would help us fix it…"
            rows={3}
            className="w-full resize-none rounded-xl border border-neutral-3 bg-white px-3 py-2 text-sm text-neutral-1 placeholder:text-neutral-2 focus:border-interactive-1 focus:outline-none"
          />
        </label>

        <DialogFooter>
          <Button
            onClick={() => void handleSubmit()}
            disabled={selectedCategories.length === 0 || isSubmitting}
          >
            {isSubmitting ? "Submitting…" : "Submit report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
